import {useCallback, useRef} from 'react';
import {t} from 'i18next';
import {toast} from 'react-toastify';
import {v4 as uuid} from 'uuid';
import {ZodError} from 'zod';
import {ExportTypes} from '../../../incase-domain/common/exportTypes';
import {IElement} from '../../../incase-domain/elements';
import {ISlide} from '../../../incase-domain/slide';
import {importElementFileContentSchema} from '../../../incase-domain/validators/elements/importElement';
import {convertElementBase64ToFileUrls} from '../../../utils/elementMediaBase64';
import {decodeExport} from '../../../utils/exportEncoding';
import {FileResult} from '../BaseFileLoader';
import {getImageSizeErrorMessage} from '../../../utils/imageUploadValidation';

export default function useNblFileReader(onLoad: (element: IElement[]) => void, getSlide: () => ISlide): (files: FileResult[]) => void {
    const onLoadRef = useRef(onLoad);
    onLoadRef.current = onLoad;

    return useCallback(
        async (files: FileResult[]) => {
            const slide = getSlide();
            const results = await Promise.all(
                files.map(async ({reader}): Promise<IElement | null> => {
                    try {
                        const contents = reader.result as string;
                        const parsedContents: {data: IElement; type: ExportTypes} = JSON.parse(decodeExport(contents));
                        importElementFileContentSchema.parse(parsedContents);
                        const newId = uuid();
                        const newElement = {
                            ...parsedContents.data,
                            slideId: slide._id,
                            id: `${slide._id}-${newId}`,
                            keyIndex: `slide-${slide._id}-element-${newId || slide.elements.length}-inner`,
                            elementIndex: slide.elements.length,
                        };
                        return await convertElementBase64ToFileUrls(newElement);
                    } catch (e) {
                        if (e instanceof ZodError) {
                            toast.error(t('contextMenuSlide.errors.importElementWrongTypeDescription'), {toastId: 'nbl-import-pres-error'});
                        } else {
                            toast.error(getImageSizeErrorMessage(e) ?? t('toasts.importElementError'), {toastId: 'nbl-import-media-error'});
                        }
                        return null;
                    }
                })
            );
            const validElements = results.filter((element): element is IElement => !!element);
            onLoadRef.current(validElements);
        },
        [getSlide]
    );
}
